import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowLeft, CheckCircle2, Download, ArrowRight } from "lucide-react";
import { CATEGORIES } from "@/data/categories";
import { SITE } from "@/lib/site";
import QuoteForm from "@/components/QuoteForm";

export default function ProductDetail() {
  const { slug } = useParams();
  const cat = CATEGORIES.find((c) => c.slug === slug);

  if (!cat) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="product-not-found">
        <h1 className="font-display text-3xl font-extrabold">Category not found</h1>
        <Link to="/products" className="text-orange-600 mt-4 inline-block">Back to products</Link>
      </main>
    );
  }

  const Icon = Icons[cat.icon] || Icons.Package;
  const others = CATEGORIES.filter((c) => c.slug !== cat.slug).slice(0, 4);

  return (
    <main data-testid={`product-detail-${cat.slug}`} className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
      <section className="pt-10 md:pt-14">
        <Link to="/products" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="pd-back">
          <ArrowLeft className="w-4 h-4" /> All categories
        </Link>

        <div className="grid lg:grid-cols-12 gap-10 mt-6 pb-12 border-b border-slate-200">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-slate-50 border border-slate-200">
                <Icon className="w-6 h-6 text-slate-900" strokeWidth={1.6} />
              </span>
              <div className="text-xs uppercase tracking-widest text-slate-500">{cat.tagline}</div>
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] mt-4">
              {cat.name}
            </h1>
            <p className="text-slate-600 mt-4 text-base md:text-lg max-w-2xl leading-relaxed">{cat.description}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={`/quote?category=${encodeURIComponent(cat.name)}`} className="btn-orange inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold" data-testid="pd-quote">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={SITE.catalogue} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold">
                <Download className="w-4 h-4" /> Full Catalogue
              </a>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="rounded-2xl border border-slate-200 p-6 bg-slate-50">
              <div className="text-xs uppercase tracking-widest text-slate-500">Trusted Brands</div>
              <div className="flex flex-wrap gap-2 mt-3">
                {cat.brands.map((b) => (
                  <span key={b} className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 font-medium">{b}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="text-xs uppercase tracking-widest text-slate-500">Sub-products</div>
        <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-2">What we supply in this category</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {cat.items.map((it, i) => (
            <motion.div key={it} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.35, delay: i * 0.03 }}
              className="p-5 rounded-2xl border border-slate-200 bg-white flex items-start gap-3 hover:shadow-md transition">
              <CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
              <span className="text-sm text-slate-800 leading-snug">{it}</span>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-14 border-t border-slate-200 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Quick RFQ</div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-2">
            Request a quote for <span className="gfe-gradient-text">{cat.name}</span>
          </h2>
          <p className="text-slate-600 mt-3">Send us your spec — brand, part number, quantity and delivery window. We&apos;ll respond within 24 hours.</p>
        </div>
        <div className="lg:col-span-7 rounded-3xl border border-slate-200 p-6 md:p-10 bg-white">
          <QuoteForm defaultCategory={cat.name} />
        </div>
      </section>

      <section className="py-14 border-t border-slate-200">
        <div className="flex items-end justify-between mb-8">
          <h3 className="font-display text-2xl md:text-3xl font-bold">Related categories</h3>
          <Link to="/products" className="text-sm text-slate-600 hover:text-slate-900">View all</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {others.map((o) => {
            const IconO = Icons[o.icon] || Icons.Package;
            return (
              <Link key={o.slug} to={`/products/${o.slug}`} data-testid={`related-${o.slug}`}
                className="group p-5 rounded-2xl border border-slate-200 hover:border-slate-400 hover:-translate-y-0.5 transition bg-white">
                <IconO className="w-5 h-5 text-slate-800" strokeWidth={1.6} />
                <div className="font-display font-bold text-slate-900 mt-4">{o.name}</div>
                <div className="text-xs text-slate-500 mt-1">{o.tagline}</div>
              </Link>
            );
          })}
        </div>
      </section>
    </main>
  );
}
